<script setup lang="ts">
const userStore = useUserStore()
const baseStore = useBaseStore()
const modalStore = useModalStore()
const userMenuOpen = ref(false)
const route = useRoute()
const router = useRouter()

const localePath = useLocalePath()
const { closeTopModal } = useCloseTopModal()

// Detect if running on iOS or Android using baseStore.platform
const isMobileApp = computed(() => {
  return baseStore.platform === 'ios' || baseStore.platform === 'android'
})

// Check if there's a previous route available
const canGoBack = computed(() => {
  return true
})

// Function to go back
function goBack() {
  if (import.meta.client) {
    if (closeTopModal()) {
      return
    }
    router.back()
  }
}

const userActions = computed(() => {
  const actions: any[] = [
    {
      label: 'Mon compte & réglages',
      icon: 'i-ic-round-manage-accounts',
      to: localePath({ name: 'UserProfilePage' }),
    },
  ]

  // Dynamically add based on current reactive state
  if (userStore.user?.retailer === 1) {
    actions.push({
      label: 'Accès Détaillant',
      icon: 'i-ic-round-store',
      to: localePath({ name: 'RetailerAccessPage' }),
    })
  }

  actions.push({
    label: 'Déconnexion',
    icon: 'i-ic-baseline-logout',
    onSelect: async () => {
      await userStore.clearUser()
      window.location.reload()
    },
  })

  return actions
})

// Formatage de la date de fin d'essai
const formattedTrialEndDate = computed(() => {
  if (!userStore.user?.trial_ends_at)
    return ''

  const date = new Date(userStore.user.trial_ends_at)
  const day = date.getDate()
  const month = date.toLocaleDateString('fr-FR', { month: 'long' })

  return `${day} ${month}`
})
</script>

<template>
  <div>
    <header
      id="top-bar"
      class="bg-tomate xl:bg-default/85 xl:backdrop-blur border-b border-default sticky top-0 z-50 border-0 xl:shadow-xs"
    >
      <div class="safe-area-top" aria-hidden="true" />
      <div class="h-13 xl:h-16 flex justify-between items-center px-3 xl:px-5">
      <div class="flex-1 max-w-2xl">
        <NuxtLink :to="$localePath({ name: 'IndexPage' })" class="xl:hidden">
          <img class="h-7 w-auto shrink-0" src="/images/logo-white.svg">
        </NuxtLink>
        <div class="hidden xl:block">
          <SearchInput
            size="xl"
            placeholder="Rechercher des rabais, produits ou recettes..."
            max-height="max-h-96"
            result-padding="px-4 py-3"
            image-size="w-16 h-16"
            icon-size="w-8 h-8"
            font-size="text-sm"
          />
        </div>
      </div>

      <div class="flex space-x-2">
        <UColorModeButton class="hidden" />
        <UDropdownMenu
          v-if="userStore.user"
          v-model:open="userMenuOpen"
          size="lg"
          :items="userActions"
          :content="{
            align: 'end',
            side: 'bottom',
            sideOffset: 1,
          }"
          :ui="{
            content: 'w-70',
          }"
          :modal="false"
        >
          <template #content-top>
            <div class="border-b border-gray-300 p-3 text-sm bg-sorbet-50">
              <div class="text-md font-normal mb-2 xl:hidden">
                <span>Bonjour</span>, <span class="text-md font-semibold">{{ userStore.user.firstname }}</span>
              </div>
              <div v-if="userStore.user.extra == 1 && userStore.user.extra_active_trial == 1">
                <ULink :to="localePath({ name: 'ExtraPage' })" class="inline-flex px-3 py-2 rounded-lg text-sm font-bold items-center bg-bleuet text-sorbet hover:text-sorbet hover:bg-bleuet-400">
                  <UIcon name="i-mdi-timer-outline" class="size-4 mr-1.5 text-yellow-300" />
                  En période d'essai Extra
                </ULink>
                <div class="text-sm leading-4 text-poivre-600 my-2 p-2 border-dashed border-1 border-tomate-400 rounded-lg">
                  Votre essai de l'Extra se termine le <span class="underline font-medium">{{ formattedTrialEndDate }}</span> et votre compte basculera en mode gratuit (limité) à cette date.
                </div>
                <div class="text-sm leading-4 text-poivre-600 my-2">
                  L'abonnement Extra vous donne accès à de <ULink :to="localePath({ name: 'ExtraPage' })" class="underline text-tomate-400 hover:text-tomate-500 inline-flex">
                    nombreux avantages
                  </ULink> pour économiser temps et argent. Commandez avant la fin de votre essai pour ne pas perdre vos avantages.
                </div>

                <UButton :to="localePath({ name: 'ExtraPage' })" color="primary" size="sm" class="">
                  <UIcon name="i-mdi-cart" class="mr-0.5" /> Commandez votre abonnement
                </UButton>
              </div>
              <div v-else-if="userStore.user.extra == 1" class="items-center">
                <ULink
                  :to="localePath({ name: 'ExtraPage' })"
                  class="member-extra-badge relative overflow-hidden inline-flex px-3 py-2 rounded-lg text-sm font-bold items-center text-poivre-600 shadow-sm hover:shadow-lg transition-shadow"
                  style="background: linear-gradient(135deg, #e5d9a2 0%, #f0e8be 40%, #f7f2df 50%, #f0e8be 60%, #e5d9a2 100%)"
                >
                  <UIcon name="i-mdi-star" class="mr-1.5 size-4 text-tomate-600/90" /> Membre Extra
                  <div class="member-extra-shine absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent pointer-events-none" />
                </ULink>
                <div class="text-sm leading-4 text-poivre-600 my-2">
                  Avec votre abonnement vous profitez de <ULink :to="localePath({ name: 'ExtraPage' })" class="inline-flex underline text-tomate-400 hover:text-tomate-500">
                    nombreux avantages
                  </ULink> pour économiser temps et argent. Merci de votre support!
                </div>
              </div>
              <div v-else-if="userStore.user.extra == 0 && userStore.user.trial_status > 0">
                <ULink class="inline-flex px-3 py-2 rounded-lg text-sm font-bold text-poivre-600 bg-poivre-100/80 hover:bg-poivre-200/80 transition-colors" :to="localePath({ name: 'ExtraPage' })">
                  <UIcon name="i-mdi-speedometer-slow" class="size-4 mr-1.5 text-poivre-500" /> Membre gratuit (limité)
                </ULink>
                <div class="text-sm leading-4 text-poivre-600 my-2">
                  L'abonnement Extra vous donne accès à de <ULink class="underline text-tomate-400 inline-flex" :to="localePath({ name: 'ExtraPage' })">
                    nombreux avantages
                  </ULink> pour économiser temps et argent.
                </div>
                <UButton :to="localePath({ name: 'ExtraPage' })" color="primary" size="sm" class="" @click="userMenuOpen = false;">
                  <UIcon name="i-mdi-star" class="mr-0.5" /> Découvrez l'Extra
                </UButton>
              </div>
              <div v-else>
                <ULink class="inline-flex px-3 py-2 rounded-lg text-sm font-bold text-poivre-600 bg-poivre-100/80 hover:bg-poivre-200/80 transition-colors" :to="localePath({ name: 'ExtraPage' })">
                  <UIcon name="i-mdi-speedometer-slow" class="size-4 mr-1.5 text-poivre-500" /> Membre gratuit (limité)
                </ULink>
                <div class="text-sm leading-4 text-poivre-600 my-2">
                  L'abonnement Extra vous donne accès à de <ULink class="underline text-tomate-400 inline-flex hover:text-tomate-500" :to="localePath({ name: 'ExtraPage' })">
                    nombreux avantages
                  </ULink> pour économiser temps et argent.
                </div>
                <div class="text-sm leading-4 text-green-600 my-2">
                  Profitez d'un essai gratuit de 14 jours sans obligation ni carte de crédit.
                </div>
                <UButton :to="localePath({ name: 'ExtraPage' })" color="primary" size="sm" class="">
                  <UIcon name="i-mdi-star" class="mr-0.5" /> Essayer l'Extra
                </UButton>
              </div>
            </div>
          </template>
          <button class="flex max-w-xs items-center text-sm rounded-full h-11 pl-3 xl:border-1 xl:border-gray-300 hover:bg-sorbet hover:border-tomate">
            <span class="inline-block relative h-6 w-6">
              <UIcon name="i-ic-baseline-account-circle" class="size-6 text-white xl:text-poivre-700" />
            </span>
            <div class="ml-2 hidden xl:block text-left leading-4 text-sm">
              <span class="text-md font-normal">Bonjour</span> <span class="text-md font-semibold">{{ userStore.user.firstname }}</span><br>

              <span v-if="userStore.user.extra == 1 && userStore.user.extra_active_trial == 1" class="text-xs font-normal text-poivre-500">En période d'essai Extra...</span>
              <span v-else-if="userStore.user.extra == 1" class="text-xs font-bold text-tomate-400">Membre Extra</span>
              <span v-else class="text-xs font-normal text-poivre-500">Membre gratuit (limité)</span>
            </div>

            <div v-if="userStore.user" class="items-center hidden xl:flex">
              <UIcon
                name="i-mdi-menu-down" class="transition-transform duration-200 ease-in-out size-6 mx-2" :class="{
                  'rotate-180 transform': userMenuOpen,
                  'transform rotate-0': !userMenuOpen,
                }"
              />
            </div>
          </button>
        </UDropdownMenu>
        <button v-else class="flex max-w-xs items-center text-sm rounded-full h-11 pl-3 xl:border-1 xl:border-gray-300 hover:bg-sorbet hover:border-tomate" @click.prevent="userMenuOpen = false;modalStore.openLoginModal();">
          <span class="inline-block h-6 relative">
            <UIcon name="i-ic-baseline-account-circle" class="size-6 text-white xl:text-poivre-700" />
          </span>

          <div class="ml-2 mr-5 hidden xl:block text-left leading-4 text-sm">
            <span class="font-normal text-poivre-700"><span class="font-semibold text-poivre-700">Connectez-vous</span> ou</span><br>
            <span class="font-semibold text-poivre-700">créez un compte</span> gratuitement
          </div>
        </button>
        <ULink
          v-if="!userStore.user || (userStore.user && userStore.user.extra == 0) || (userStore.user && userStore.user.extra == 1 && userStore.user.extra_active_trial == 1)"
          :to="localePath({ name: 'ExtraPage' })"
          class="max-w-xs items-center text-sm rounded-full h-11 pl-3 pr-5 border-1 bg-tomate border-tomate-500 hover:bg-tomate-600 hover:border-tomate text-white hover:text-white hidden xl:flex"
        >
          <span class="inline-block h-6 w-6 relative overflow-hidden">

            <UIcon name="i-mdi-star" class="h-full w-full text-sorbet" />
          </span>
          <div class="ml-2 hidden xl:block text-left leading-4 text-sm">
            <span class="text-md font-medium">Découvrir l'Extra</span><br>
            <span class="text-xs font-normal">Essai gratuit 14 jours</span>
          </div>
        </ULink>
        <UButton size="xl" icon="i-material-symbols-menu-rounded" variant="ghost" color="white" class="xl:hidden" @click="modalStore.openMobileSidebar" />
      </div>
      </div>
    </header>
    <div
      class="xl:hidden px-2 py-1 bg-gray-50/75 backdrop-blur relative" :class="[
        route.name === 'FlyerPage___fr' ? 'hidden' : 'flex',
      ]"
    >
      <!-- Back button for iOS-Android when previous route is available -->
      <UButton
        v-if="isMobileApp && canGoBack"
        icon="i-mdi-arrow-left"
        variant="ghost"
        color="neutral"
        size="lg"
        class="mr-2 flex-shrink-0"
        @click="goBack"
      />

      <SearchInput
        size="lg"
        placeholder="Rechercher des rabais ou recettes..."
        max-height="max-h-80"
        result-padding="px-3 py-2.5"
        image-size="w-12 h-12"
        icon-size="w-6 h-6"
        font-size="text-sm"
      />
    </div>
  </div>
</template>

<style scoped>
@keyframes member-extra-shine {
  0% {
    transform: translateX(-100%) skewX(-12deg);
  }
  100% {
    transform: translateX(200%) skewX(-12deg);
  }
}

.member-extra-shine {
  animation: member-extra-shine 3s ease-in-out infinite;
}
</style>
